import React from 'react';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
} from 'lucide-react';
import {
  DayPicker,
  DropdownProps,
  type DayPickerProps,
} from 'react-day-picker';
import {
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  BaseSelect,
} from '@sib/ui';
import { CALENDAR_END_MONTH, CALENDAR_START_MONTH } from '@sib/utils';
import { cn } from '../utils';
import { Button, buttonVariants } from './Button';

export type CalendarProps = DayPickerProps;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'dropdown',
  startMonth = CALENDAR_START_MONTH,
  endMonth = CALENDAR_END_MONTH,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      captionLayout={captionLayout}
      startMonth={startMonth}
      endMonth={endMonth}
      className={cn('p-3', className)}
      classNames={{
        months: 'relative flex flex-col sm:flex-row',
        month: 'gap-2 flex flex-col',

        nav: 'flex items-center justify-between absolute w-full z-10 px-1',
        button_previous: cn(
          buttonVariants({
            variant: 'ghost',
            className:
              'hidden size-7 bg-transparent p-0 hover:bg-muted hover:text-muted-foreground disabled:border-[0.5px] disabled:text-muted-foreground',
          })
        ),
        button_next: cn(
          buttonVariants({
            variant: 'ghost',
            className:
              'hidden size-7 bg-transparent p-0 hover:bg-muted hover:text-muted-foreground disabled:border-[0.5px] disabled:text-muted-foreground',
          })
        ),
        dropdowns: 'flex justify-start items-start gap-2 z-20 w-[100%]',

        month_caption:
          'flex justify-center items-center  border-b border-border pb-2',
        caption_label: 'text-sm font-medium',
        month_grid: 'border-collapse space-y-1',
        weekdays: 'flex w-full',
        weekday:
          'text-primary w-9 font-semibold text-xs text-center w-full border-none bg-transparent pb-2 pt-1',
        weeks: '',
        week: 'flex',
        day: 'p-0 border-none',
        outside: 'bg-accent/40',
        range_middle: 'bg-accent last:rounded-e-md first:rounded-s-md',
        range_start: 'bg-accent rounded-s-md',
        range_end: 'bg-accent rounded-e-md',
        ...classNames,
      }}
      components={{
        Dropdown: ({ value, onChange, options, disabled }: DropdownProps) => {
          const handleChange = (value: string) => {
            const changeEvent = {
              target: { value },
            } as React.ChangeEvent<HTMLSelectElement>;
            onChange?.(changeEvent);
          };

          const selected = (options || [])?.find(
            child => child.value === value
          );

          return (
            <BaseSelect value={value?.toString()} onValueChange={handleChange}>
              <SelectTrigger
                className={cn(
                  'w-[110px] border-none pr-1.5 text-sm font-medium focus:ring-0'
                )}
              >
                <SelectValue className={cn('w-full', disabled && 'bg-muted')}>
                  {selected?.label}
                </SelectValue>
              </SelectTrigger>
              <SelectContent position="popper" align="center">
                <div className="max-h-80 overflow-y-auto">
                  {options?.map(({ value, label, disabled }, id) => (
                    <SelectItem
                      key={`${value}-${id}`}
                      value={value?.toString()}
                      disabled={disabled}
                    >
                      {label}
                    </SelectItem>
                  ))}
                </div>
              </SelectContent>
            </BaseSelect>
          );
        },

        DayButton({ modifiers, className, ...buttonProps }) {
          return (
            <Button
              variant="ghost"
              className={cn(
                className,
                'hover:bg-secondary bg-surface-subtle m-1 size-9 rounded-md text-xs font-normal hover:opacity-100 hover:brightness-100',
                modifiers?.today && 'bg-secondary/60 border',
                modifiers?.selected &&
                  'bg-secondary text-primary focus:bg-secondary focus:text-primary',
                modifiers?.outside &&
                  'text-primary pointer-events-none bg-transparent opacity-20',
                modifiers?.disabled && 'text-muted-foreground opacity-50',
                modifiers?.hidden && 'invisible',
                modifiers.range_middle &&
                  'text-primary rounded-none bg-[#31D1D440] first:rounded-s-md last:rounded-e-md',
                modifiers.outside &&
                  modifiers.selected &&
                  'bg-accent/40 text-muted-foreground'
              )}
              {...buttonProps}
              aria-selected={modifiers.selected || buttonProps['aria-selected']}
              aria-disabled={modifiers.disabled || buttonProps['aria-disabled']}
              aria-hidden={modifiers.hidden || buttonProps['aria-hidden']}
            />
          );
        },
        Chevron({ orientation, disabled, className }) {
          const Component =
            orientation === 'left'
              ? ChevronLeft
              : orientation === 'right'
                ? ChevronRight
                : orientation === 'up'
                  ? ChevronUp
                  : ChevronDown;

          return (
            <Component
              className={cn('h-4 w-4', className)}
              aria-disabled={disabled}
            />
          );
        },
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
